import { ReactElement, useMemo } from 'react';
import Unit from '@src/sld/components/Unit/Unit';
import { UnitConfig } from '@src/sld/types';
import VerticalLine from '@src/sld/components/Line/VerticalLine';
import HorizontalLine from '@src/sld/components/Line/HorizontalLine';
import { getUnitConfig } from '@src/sld/utils';
import { useUnitContext } from './UnitContext';
import useSubscription from '@src/hooks/useSubscription';
import { DataPointWithValue } from '@src/types/DataPoint';

function Switch1Units(): ReactElement {
  const { siteMetaUnits, unitWidgetConfig, getDataPointValue } =
    useUnitContext();

  const sectionUnits = useMemo(
    () => siteMetaUnits.slice(0, 6),
    [siteMetaUnits],
  );

  const dataPointsToSubscribe = useMemo(
    () =>
      (unitWidgetConfig?.dataPoints || []).filter((dp) =>
        sectionUnits.includes(dp.controllerId),
      ),
    [unitWidgetConfig, sectionUnits],
  );

  const dataPointsWithValue: DataPointWithValue[] = useSubscription(
    dataPointsToSubscribe,
  );

  const units = useMemo(() => {
    return (unitWidgetConfig?.devices
      .map((device) => {
        if (sectionUnits.includes(device.engineeringId)) {
          return {
            unitLabel: device.name,
            kw: getDataPointValue(
              device.engineeringId,
              'kW',
              dataPointsWithValue,
            ),
            kvar: getDataPointValue(
              device.engineeringId,
              'kVAR',
              dataPointsWithValue,
            ),
            soc: getDataPointValue(
              device.engineeringId,
              'SOC',
              dataPointsWithValue,
            ),
            racksonline: getDataPointValue(
              device.engineeringId,
              'racks_online',
              dataPointsWithValue,
            ),
            invstatus: getDataPointValue(
              device.engineeringId,
              'InvStatus',
              dataPointsWithValue,
            ),
            runmode: getDataPointValue(
              device.engineeringId,
              'RunMode',
              dataPointsWithValue,
            ),
          };
        }
      })
      .filter(Boolean) || []) as UnitConfig[];
  }, [unitWidgetConfig, sectionUnits, dataPointsWithValue, getDataPointValue]);

  if (!units || units.length === 0) {
    return <></>;
  }

  return (
    <g transform='translate(-50,-400)'>
      {/* top vertical line */}
      <VerticalLine
        length={41}
        color='white'
        width={0.5}
        translateXCoordinate={430}
        translateYCoordinate={715}
      />

      {/* top horizontal line */}
      <HorizontalLine
        length={400}
        color='white'
        width={0.5}
        translateXCoordinate={225}
        translateYCoordinate={756}
      />

      {/* units */}
      <VerticalLine
        length={25}
        color='white'
        width={0.5}
        translateXCoordinate={225}
        translateYCoordinate={756}
      />
      <Unit
        unitConfig={getUnitConfig(units[0])}
        rectangleXCoordinate='190'
        rectangleYCoordinate='782'
        textStartingXCoordinate={195}
        textStartingYCoordinate={780}
        lineTransformXCoordinate={125}
        lineTransformYCoordinate={757}
        skipHorizontalLine
      />

      <VerticalLine
        length={75}
        color='white'
        width={0.5}
        translateXCoordinate={300}
        translateYCoordinate={756}
      />
      <Unit
        unitConfig={getUnitConfig(units[1])}
        rectangleXCoordinate='270'
        rectangleYCoordinate='782'
        textStartingXCoordinate={275}
        textStartingYCoordinate={780}
        lineTransformXCoordinate={215}
        lineTransformYCoordinate={757}
      />

      <VerticalLine
        length={25}
        color='white'
        width={0.5}
        translateXCoordinate={390}
        translateYCoordinate={756}
      />
      <Unit
        unitConfig={getUnitConfig(units[2])}
        rectangleXCoordinate='350'
        rectangleYCoordinate='782'
        textStartingXCoordinate={355}
        textStartingYCoordinate={780}
        lineTransformXCoordinate={280}
        lineTransformYCoordinate={757}
      />

      <VerticalLine
        length={25}
        color='white'
        width={0.5}
        translateXCoordinate={470}
        translateYCoordinate={756}
      />
      <Unit
        unitConfig={getUnitConfig(units[3])}
        rectangleXCoordinate='430'
        rectangleYCoordinate='782'
        textStartingXCoordinate={435}
        textStartingYCoordinate={780}
        lineTransformXCoordinate={345}
        lineTransformYCoordinate={757}
      />

      <VerticalLine
        length={25}
        color='white'
        width={0.5}
        translateXCoordinate={550}
        translateYCoordinate={756}
      />
      <Unit
        unitConfig={getUnitConfig(units[4])}
        rectangleXCoordinate='510'
        rectangleYCoordinate='782'
        textStartingXCoordinate={515}
        textStartingYCoordinate={780}
        lineTransformXCoordinate={410}
        lineTransformYCoordinate={757}
      />

      <VerticalLine
        length={25}
        color='white'
        width={0.5}
        translateXCoordinate={625}
        translateYCoordinate={756}
      />
      <Unit
        unitConfig={getUnitConfig(units[5])}
        rectangleXCoordinate='590'
        rectangleYCoordinate='782'
        textStartingXCoordinate={595}
        textStartingYCoordinate={780}
        lineTransformXCoordinate={475}
        lineTransformYCoordinate={757}
      />
    </g>
  );
}

export default Switch1Units;
